"use client";

import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient, garagemAuth } from "@/db/auth/client";
import { toast } from "@garagem/ui/components/sonner";
import { authQueryKeys } from "./query-keys";
import { useOrganization } from "./organization";
import { useTRPC } from "@/lib/trpc/client";
import { getUserFriendlyErrorMessage } from "./parse-auth-error";
import { captureException } from "@web/lib/analytics/posthog-client";

// Team hooks
export function useTeams() {
  // Reuse organization data instead of making a separate call
  // This prevents duplicate getFullOrganization requests
  const { data: organization, ...orgQuery } = useOrganization();
  
  return {
    data: organization?.teams || [],
    ...orgQuery,
  };
}

// Team members hook
export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: [...authQueryKeys.organization.teams(), teamId, "members"],
    queryFn: async () => {
      const response = await authClient.organization.listTeamMembers?.({
        query: { teamId },
      });
      if (!response || response.error) return [];
      return response.data || [];
    },
    enabled: !!teamId,
  });
}

// Hook to fetch all teams with their members
export function useTeamsWithMembers() {
  const trpc = useTRPC();

  // Use backend endpoint that returns teams with members enriched with user names
  const { data: teamsWithMembers = [], isLoading, error, refetch } = trpc.auth.getTeamsWithMembers.useQuery(
    undefined,
    {
      // Prevent refetch on mount/window focus to avoid duplicate requests
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  return {
    data: teamsWithMembers,
    isLoading,
    error,
    refetch,
  };
}

export function useCreateTeam() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const utils = trpc.useUtils();

  return trpc.auth.teams.createWithMembersAndArea.useMutation({
    onSuccess: async () => {
      toast.success("Equipe criada com sucesso!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.teams(),
      });
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.details(),
      });
      utils.auth.getTeamsWithMembers.invalidate();
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: authQueryKeys.organization.teams(),
        }),
        queryClient.refetchQueries({
          queryKey: authQueryKeys.organization.details(),
        }),
      ]);
    },
    onError: (error) => {
      toast.error("Erro ao criar equipe", {
        description: error?.message || "Ocorreu um erro ao criar a equipe.",
      });
    },
  });
}

const UPDATE_ALREADY_IN_FLIGHT = Symbol("update-team-in-flight");

export function useUpdateTeam() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const utils = trpc.useUtils();
  const inFlight = useRef(false);

  return useMutation({
    retry: false,
    mutationFn: async ({ teamId, name, custom }: { teamId: string; name?: string; custom?: Record<string, unknown> }) => {
      // A second click before `isPending` disables Salvar must not send another PATCH.
      if (inFlight.current) return UPDATE_ALREADY_IN_FLIGHT;
      inFlight.current = true;
      try {
        const response = await garagemAuth.organization.updateTeamBypass({
          teamId,
          ...(name !== undefined && { name }),
          ...(custom !== undefined && { custom }),
        });
        if (response.error) throw response.error;
        return response.data;
      } finally {
        inFlight.current = false;
      }
    },
    onSuccess: (data) => {
      if (data === UPDATE_ALREADY_IN_FLIGHT) return;
      toast.success("Equipe atualizada com sucesso!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.teams(),
      });
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.details(),
      });
      // Also invalidate tRPC queries for teams with members
      utils.auth.getTeamsWithMembers.invalidate();
    },
    onError: (error, variables) => {
      captureException(error, {
        source: "mutation",
        action: "update_team",
        component: "useUpdateTeam",
        userFacing: true,
        updatesName: variables.name !== undefined,
        updatesCustom: variables.custom !== undefined,
      });
      toast.error("Erro ao atualizar equipe", {
        description: error?.message || "Ocorreu um erro ao atualizar a equipe.",
      });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const utils = trpc.useUtils();

  return useMutation({
    mutationFn: async ({ teamId }: { teamId: string }) => {
      const response = await authClient.organization.removeTeam({ teamId });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("Equipe removida!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.teams(),
      });
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.details(),
      });
      // Also invalidate tRPC queries for teams with members
      utils.auth.getTeamsWithMembers.invalidate();
    },
    onError: () => {
      toast.error("Erro ao remover equipe");
    },
  });
}

// Team member hooks
// Uses custom garagem-auth endpoint that bypasses Better Auth's hardcoded owner/admin check
// This allows roles like "gerente" to manage team members
export function useAddTeamMember() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const utils = trpc.useUtils();

  return useMutation({
    mutationFn: async ({
      teamId,
      userId,
    }: {
      teamId: string;
      userId: string;
    }) => {
      // Use custom endpoint that checks ADMIN_ROLES (includes gerente)
      const response = await garagemAuth.organization.addTeamMemberBypass({
        teamId,
        userId,
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (_data, _variables) => {
      toast.success("Membro adicionado à equipe!");
      // Invalidate all organization queries to refresh team members
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
      // Also invalidate the specific team members query
      queryClient.invalidateQueries({
        queryKey: [...authQueryKeys.organization.teams(), "all-members"],
      });
      // Also invalidate tRPC queries for teams with members
      utils.auth.getTeamsWithMembers.invalidate();
    },
    onError: (error) => {
      const errorMessage = getUserFriendlyErrorMessage(error);
      toast.error(errorMessage.title, {
        description: errorMessage.description,
      });
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const utils = trpc.useUtils();

  return useMutation({
    mutationFn: async ({
      teamId,
      userId,
    }: {
      teamId: string;
      userId: string;
    }) => {
      // Use custom endpoint that checks ADMIN_ROLES (includes gerente)
      const response = await garagemAuth.organization.removeTeamMemberBypass({
        teamId,
        userId,
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (_data, _variables) => {
      toast.success("Membro removido da equipe!");
      // Invalidate all organization queries to refresh team members
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
      // Also invalidate the specific team members query
      queryClient.invalidateQueries({
        queryKey: [...authQueryKeys.organization.teams(), "all-members"],
      });
      // Also invalidate tRPC queries for teams with members
      utils.auth.getTeamsWithMembers.invalidate();
    },
    onError: (error) => {
      const errorMessage = getUserFriendlyErrorMessage(error);
      toast.error(errorMessage.title, {
        description: errorMessage.description,
      });
    },
  });
}

